import {
  lazy,
  ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DataObjectIcon from "@mui/icons-material/DataObject";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import { Title, useGetIdentity, useNotify } from "react-admin";
import {
  ApiGatewayError,
  CreateRemoteConfigRuleRequest,
  PatchRemoteConfigRuleRequest,
  Platform,
  RemoteConfigAuditAction,
  RemoteConfigAuditItem,
  RemoteConfigAuditValueState,
  RemoteConfigRule,
  remoteConfigProxy,
} from "./apiGateway";

type ValueType = "object" | "array" | "string" | "number" | "boolean" | "null";

type RuleFormState = {
  key: string;
  valueType: ValueType;
  valueText: string;
  booleanValue: boolean;
  platform: Platform | "";
  minAppVersion: string;
  maxAppVersion: string;
  priority: string;
  enabled: boolean;
  description: string;
};

type RuleFilters = {
  key?: string;
  platform?: Platform;
  enabled?: boolean;
};

type ResolveParams = {
  platform?: Platform;
  appVersion?: string;
};

const KEY_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;
const VERSION_PATTERN = /^\d+\.\d+\.\d+$/;
const NUMBER_PATTERN = /^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?$/;
const JsonCodeEditor = lazy(() => import("./JsonCodeEditor"));
const WRITE_ROLES = new Set(["manager-admin", "developer"]);
const READ_ROLES = new Set([
  "manager-admin",
  "developer",
  "moderator-admin",
  "moderator",
]);
const AUDIT_PAGE_LIMIT = 20;
const AUDIT_ACTION_META: Record<
  RemoteConfigAuditAction,
  {
    label: string;
    color: "default" | "primary" | "success" | "error" | "warning";
  }
> = {
  CREATED: { label: "Создано", color: "success" },
  DELETED: { label: "Удалено", color: "error" },
  ENABLED: { label: "Включено", color: "success" },
  DISABLED: { label: "Выключено", color: "warning" },
  UPDATED: { label: "Отредактировано", color: "primary" },
};

function buildRemoteConfigFileName(platform: Platform, appVersion?: string) {
  const versionSuffix = appVersion ? `-${appVersion}` : "";
  return `remote-config-${platform.toLowerCase()}${versionSuffix}.json`;
}

const EMPTY_FORM: RuleFormState = {
  key: "",
  valueType: "boolean",
  valueText: "",
  booleanValue: false,
  platform: "",
  minAppVersion: "",
  maxAppVersion: "",
  priority: "0",
  enabled: true,
  description: "",
};

const platformLabel = (platform: Platform | null | "") => {
  if (!platform) {
    return "Все платформы";
  }
  return platform === "IOS" ? "iOS" : platform === "WEB" ? "Web" : "Android";
};

const valueTypeLabel: Record<ValueType, string> = {
  object: "JSON",
  array: "Массив",
  string: "Строка",
  number: "Число",
  boolean: "Boolean",
  null: "Null",
};
const SELECTABLE_VALUE_TYPES: ValueType[] = [
  "object",
  "array",
  "string",
  "number",
  "boolean",
  "null",
];

function emptyValueForType(type: ValueType) {
  return {
    valueText:
      type === "object" ? "{\n  \n}" : type === "array" ? "[\n  \n]" : "",
    booleanValue: false,
  };
}

function validateKeyValue(value: string) {
  const normalized = value.trim();
  if (!normalized) return "Укажите ключ конфигурации";
  if (!KEY_PATTERN.test(normalized)) {
    return "Начните с латинской буквы a–z; используйте только a–z, 0–9 и _";
  }
  return "";
}

function normalizeVersionInput(value: string) {
  return value
    .replace(/[^\d.]/g, "")
    .split(".")
    .slice(0, 3)
    .join(".");
}

function validateVersionValue(value: string) {
  if (!value) return "";
  return VERSION_PATTERN.test(value)
    ? ""
    : "Введите три числовые части, например 2.5.1";
}

function validateNumberValue(value: string) {
  const normalized = value.trim();
  if (!normalized) return "Укажите числовое значение";
  if (
    !NUMBER_PATTERN.test(normalized) ||
    !Number.isFinite(Number(normalized))
  ) {
    return "Введите корректное число";
  }
  return "";
}

function validateJsonValue(value: string, type: "object" | "array") {
  if (!value.trim()) return "Введите JSON";

  let parsedValue: unknown;
  try {
    parsedValue = JSON.parse(value);
  } catch {
    return "Некорректный JSON";
  }

  if (type === "array") {
    return Array.isArray(parsedValue) ? "" : "Ожидается JSON-массив";
  }

  return parsedValue !== null &&
    !Array.isArray(parsedValue) &&
    typeof parsedValue === "object"
    ? ""
    : "Ожидается JSON-объект";
}

function getValueType(value: unknown): ValueType {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  if (typeof value === "object") return "object";
  if (typeof value === "number") return "number";
  if (typeof value === "boolean") return "boolean";
  return "string";
}

function valueToText(value: unknown, type: ValueType) {
  if (type === "object" || type === "array") {
    return JSON.stringify(value, null, 2);
  }
  if (type === "string" || type === "number") {
    return String(value);
  }
  return "";
}

function ruleToForm(rule: RemoteConfigRule): RuleFormState {
  const valueType = getValueType(rule.value);
  return {
    key: rule.key,
    valueType,
    valueText: valueToText(rule.value, valueType),
    booleanValue: valueType === "boolean" ? Boolean(rule.value) : false,
    platform: rule.platform ?? "",
    minAppVersion: rule.minAppVersion ?? "",
    maxAppVersion: rule.maxAppVersion ?? "",
    priority: String(rule.priority),
    enabled: rule.enabled,
    description: rule.description ?? "",
  };
}

function parseValue(form: RuleFormState): unknown {
  if (form.valueType === "null") return null;
  if (form.valueType === "boolean") return form.booleanValue;
  if (form.valueType === "string") return form.valueText;
  if (form.valueType === "number") {
    const normalized = form.valueText.trim();
    const validationError = validateNumberValue(normalized);
    if (validationError) throw new Error(validationError);
    const value = Number(normalized);
    return value;
  }

  const validationError = validateJsonValue(form.valueText, form.valueType);
  if (validationError) throw new Error(validationError);
  return JSON.parse(form.valueText);
}

function compareVersions(left: string, right: string) {
  const leftParts = left.split(".").map((part) => BigInt(part));
  const rightParts = right.split(".").map((part) => BigInt(part));
  for (let index = 0; index < 3; index += 1) {
    if (leftParts[index] < rightParts[index]) return -1;
    if (leftParts[index] > rightParts[index]) return 1;
  }
  return 0;
}

function validateForm(form: RuleFormState, original: RemoteConfigRule | null) {
  const keyError = validateKeyValue(form.key);
  if (keyError) return keyError;
  const minVersionError = validateVersionValue(form.minAppVersion.trim());
  if (minVersionError) return `Минимальная версия: ${minVersionError}`;
  const maxVersionError = validateVersionValue(form.maxAppVersion.trim());
  if (maxVersionError) return `Максимальная версия: ${maxVersionError}`;
  if (
    form.minAppVersion &&
    form.maxAppVersion &&
    compareVersions(form.minAppVersion.trim(), form.maxAppVersion.trim()) > 0
  ) {
    return "Минимальная версия не может быть больше максимальной";
  }
  if (!/^-?\d+$/.test(form.priority.trim())) {
    return "Приоритет должен быть целым числом";
  }
  const priority = Number(form.priority);
  if (
    !Number.isSafeInteger(priority) ||
    priority < -10000 ||
    priority > 10000
  ) {
    return "Приоритет должен быть целым числом от -10000 до 10000";
  }
  if (form.description.length > 512) {
    return "Описание не может быть длиннее 512 символов";
  }
  if (form.valueType === "string" && form.valueText.length > 4096) {
    return "Строковое значение не может быть длиннее 4096 символов";
  }
  if (original && original.platform !== null && !form.platform) {
    return "API не позволяет очистить платформу существующего правила";
  }
  if (original?.minAppVersion && !form.minAppVersion.trim()) {
    return "API не позволяет очистить минимальную версию существующего правила";
  }
  if (original?.maxAppVersion && !form.maxAppVersion.trim()) {
    return "API не позволяет очистить максимальную версию существующего правила";
  }
  if (original?.description && !form.description.trim()) {
    return "API не позволяет очистить описание существующего правила";
  }
  if (original && original.value !== null && form.valueType === "null") {
    return "API не позволяет заменить существующее значение на null";
  }
  try {
    parseValue(form);
  } catch (error) {
    return error instanceof Error ? error.message : "Некорректное значение";
  }
  return null;
}

function sameJsonValue(left: unknown, right: unknown) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function buildPatch(
  original: RemoteConfigRule,
  form: RuleFormState,
): PatchRemoteConfigRuleRequest {
  const patch: PatchRemoteConfigRuleRequest = {};
  const value = parseValue(form);
  const platform = form.platform || null;
  const minAppVersion = form.minAppVersion.trim() || null;
  const maxAppVersion = form.maxAppVersion.trim() || null;
  const description = form.description || null;
  const priority = Number(form.priority);

  if (!sameJsonValue(value, original.value)) patch.value = value;
  if (platform !== original.platform && platform) patch.platform = platform;
  if (minAppVersion !== original.minAppVersion && minAppVersion)
    patch.minAppVersion = minAppVersion;
  if (maxAppVersion !== original.maxAppVersion && maxAppVersion)
    patch.maxAppVersion = maxAppVersion;
  if (description !== original.description && description)
    patch.description = description;
  if (priority !== original.priority) patch.priority = priority;
  if (form.enabled !== original.enabled) patch.enabled = form.enabled;
  return patch;
}

function formatRuleValue(value: unknown) {
  return JSON.stringify(value, null, 2) ?? String(value);
}

function versionRangeLabel(rule: RemoteConfigRule) {
  if (rule.minAppVersion && rule.maxAppVersion) {
    return `${rule.minAppVersion} — ${rule.maxAppVersion}`;
  }
  if (rule.minAppVersion) return `От ${rule.minAppVersion}`;
  if (rule.maxAppVersion) return `До ${rule.maxAppVersion}`;
  return "Все версии";
}

function scopeLabel(rule: RemoteConfigRule) {
  const versions = [
    rule.minAppVersion ? `от ${rule.minAppVersion}` : "",
    rule.maxAppVersion ? `до ${rule.maxAppVersion}` : "",
  ]
    .filter(Boolean)
    .join(" ");
  return `${platformLabel(rule.platform)}${versions ? `, ${versions}` : ""}, приоритет ${rule.priority}`;
}

function errorMessage(error: unknown, action: string) {
  if (error instanceof ApiGatewayError) {
    if (error.errorId === "REMOTE_CONFIG_CONFLICT" || error.status === 409) {
      return "Правило с такой комбинацией ключа, платформы, версий и приоритета уже существует";
    }
    if (error.errorId === "REMOTE_CONFIG_NOT_FOUND" || error.status === 404) {
      return "Правило больше не существует. Список обновлён";
    }
    if (error.errorId === "REMOTE_CONFIG_VALIDATION" || error.status === 400) {
      return "Сервис отклонил данные правила. Проверьте заполненные поля";
    }
    if (error.errorId === "INTERNAL_ERROR" || error.status >= 500) {
      return "Сервис Remote Config недоступен";
    }
  }
  return `Не удалось ${action}`;
}

const StatTile = ({
  label,
  value,
  icon,
  color = "primary.main",
}: {
  label: string;
  value: number;
  icon: ReactNode;
  color?: string;
}) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      minWidth: 0,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      borderRadius: 2,
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        display: "grid",
        placeItems: "center",
        borderRadius: 2,
        color,
        bgcolor: "action.hover",
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="h6" sx={{ lineHeight: 1.1, fontWeight: 700 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  </Paper>
);

const FormSection = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) => (
  <Paper
    variant="outlined"
    sx={(theme) => ({
      p: { xs: 2, sm: 2.5 },
      borderRadius: 2.5,
      bgcolor:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.common.white, 0.025)
          : "background.paper",
    })}
  >
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {description ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {description}
        </Typography>
      ) : null}
    </Box>
    {children}
  </Paper>
);

function formatAuditDate(value: string | null) {
  if (!value) return "Дата неизвестна";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Дата неизвестна";
  return date.toLocaleString("ru-RU", {
    dateStyle: "medium",
    timeStyle: "medium",
  });
}

function formatAuditActor(item: RemoteConfigAuditItem) {
  const { fullName, login, userId } = item.actor;
  if (fullName && login) return `${fullName} (${login})`;
  return fullName || login || userId || "Неизвестный пользователь";
}

function stringifyAuditValue(value: unknown) {
  if (typeof value === "string") return value;
  const serialized = JSON.stringify(value, null, 2);
  return serialized === undefined ? String(value) : serialized;
}

const AuditValue = ({
  state,
  missingLabel,
}: {
  state: RemoteConfigAuditValueState;
  missingLabel: string;
}) => (
  <Box
    component="pre"
    sx={{
      m: 0,
      p: 1.25,
      minHeight: 42,
      maxHeight: 220,
      overflow: "auto",
      whiteSpace: "pre-wrap",
      overflowWrap: "anywhere",
      borderRadius: 1.5,
      bgcolor: "action.hover",
      color: state.exists ? "text.primary" : "text.secondary",
      fontSize: "0.78rem",
      lineHeight: 1.5,
      fontStyle: state.exists ? "normal" : "italic",
    }}
  >
    {state.exists ? stringifyAuditValue(state.value) : missingLabel}
  </Box>
);

const AuditSnapshot = ({ rule }: { rule: RemoteConfigRule }) => (
  <Box
    component="pre"
    sx={{
      m: 0,
      p: 2,
      maxHeight: 360,
      overflow: "auto",
      whiteSpace: "pre-wrap",
      overflowWrap: "anywhere",
      borderRadius: 1.5,
      bgcolor: "grey.900",
      color: "grey.100",
      fontSize: "0.78rem",
      lineHeight: 1.55,
    }}
  >
    {JSON.stringify(rule, null, 2)}
  </Box>
);

export const RemoteConfigPage = () => {
  const notify = useNotify();
  const { identity, isPending } = useGetIdentity();
  const role = ((identity ?? null) as { role?: string } | null)?.role ?? "";
  const canRead = READ_ROLES.has(role);
  const canWrite = WRITE_ROLES.has(role);

  const [rules, setRules] = useState<RemoteConfigRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [activeTab, setActiveTab] = useState<"rules" | "resolve" | "audit">(
    "rules",
  );
  const [saving, setSaving] = useState(false);
  const [loadingRuleId, setLoadingRuleId] = useState<string | null>(null);
  const [draftKey, setDraftKey] = useState("");
  const [draftPlatform, setDraftPlatform] = useState<Platform | "">("");
  const [draftEnabled, setDraftEnabled] = useState<"all" | "true" | "false">(
    "all",
  );
  const [filters, setFilters] = useState<RuleFilters>({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<RemoteConfigRule | null>(null);
  const [form, setForm] = useState<RuleFormState>(EMPTY_FORM);
  const [keyTouched, setKeyTouched] = useState(false);
  const [deletingRule, setDeletingRule] = useState<RemoteConfigRule | null>(
    null,
  );
  const [resolvePlatform, setResolvePlatform] = useState<Platform | "">("");
  const [resolveVersion, setResolveVersion] = useState("");
  const [resolveResult, setResolveResult] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [resolveLoading, setResolveLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [lastResolveParams, setLastResolveParams] =
    useState<ResolveParams | null>(null);
  const [auditItems, setAuditItems] = useState<RemoteConfigAuditItem[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditLoadingMore, setAuditLoadingMore] = useState(false);
  const [auditError, setAuditError] = useState("");
  const [auditNextCursor, setAuditNextCursor] = useState<string | null>(null);
  const [auditHasNextPage, setAuditHasNextPage] = useState(false);
  const auditSentinelRef = useRef<HTMLDivElement | null>(null);
  const auditRequestInFlightRef = useRef(false);

  const loadRules = useCallback(
    async (targetFilters: RuleFilters) => {
      setLoading(true);
      setLoadError("");
      try {
        const data = await remoteConfigProxy.getList(targetFilters);
        setRules(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Remote config list error:", error);
        setRules([]);
        const message = errorMessage(error, "загрузить правила");
        setLoadError(message);
        notify(message, { type: "error" });
      } finally {
        setLoading(false);
      }
    },
    [notify],
  );

  useEffect(() => {
    if (canRead) void loadRules(filters);
  }, [canRead, filters, loadRules]);

  const loadAudit = useCallback(
    async (cursor: string | null, reset: boolean) => {
      if (!canWrite || auditRequestInFlightRef.current) return;

      auditRequestInFlightRef.current = true;
      if (reset) {
        setAuditLoading(true);
        setAuditItems([]);
        setAuditNextCursor(null);
        setAuditHasNextPage(false);
      } else {
        setAuditLoadingMore(true);
      }
      setAuditError("");

      try {
        const page = await remoteConfigProxy.getAudit({
          cursor,
          limit: AUDIT_PAGE_LIMIT,
        });
        const pageItems = Array.isArray(page?.items) ? page.items : [];
        setAuditItems((currentItems) => {
          if (reset) return pageItems;
          const knownIds = new Set(currentItems.map((item) => item.id));
          return [
            ...currentItems,
            ...pageItems.filter((item) => !knownIds.has(item.id)),
          ];
        });
        setAuditNextCursor(page?.pagination?.nextCursor ?? null);
        setAuditHasNextPage(Boolean(page?.pagination?.hasNextPage));
      } catch (error) {
        console.error("Remote config audit error:", error);
        const message = errorMessage(error, "загрузить аудит");
        setAuditError(message);
        notify(message, { type: "error" });
      } finally {
        auditRequestInFlightRef.current = false;
        setAuditLoading(false);
        setAuditLoadingMore(false);
      }
    },
    [canWrite, notify],
  );

  useEffect(() => {
    if (activeTab === "audit" && canWrite) {
      void loadAudit(null, true);
    }
  }, [activeTab, canWrite, loadAudit]);

  useEffect(() => {
    if (
      activeTab !== "audit" ||
      !canWrite ||
      !auditHasNextPage ||
      !auditNextCursor ||
      auditError
    ) {
      return;
    }

    const target = auditSentinelRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void loadAudit(auditNextCursor, false);
        }
      },
      { rootMargin: "240px 0px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [
    activeTab,
    auditError,
    auditHasNextPage,
    auditNextCursor,
    canWrite,
    loadAudit,
  ]);

  const runResolve = useCallback(
    async (params: ResolveParams, showError = true) => {
      setResolveLoading(true);
      try {
        const data = await remoteConfigProxy.resolve(params);
        setResolveResult(data ?? {});
        setLastResolveParams(params);
      } catch (error) {
        console.error("Remote config resolve error:", error);
        setResolveResult(null);
        if (showError)
          notify(errorMessage(error, "получить итоговый конфиг"), {
            type: "error",
          });
      } finally {
        setResolveLoading(false);
      }
    },
    [notify],
  );

  const refreshAfterMutation = useCallback(async () => {
    await loadRules(filters);
    if (lastResolveParams) await runResolve(lastResolveParams, false);
  }, [filters, lastResolveParams, loadRules, runResolve]);

  const groups = useMemo(() => {
    const grouped = new Map<string, RemoteConfigRule[]>();
    rules.forEach((rule) =>
      grouped.set(rule.key, [...(grouped.get(rule.key) ?? []), rule]),
    );
    return Array.from(grouped.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, items]) => ({
        key,
        items: items.sort(
          (left, right) =>
            right.priority - left.priority ||
            platformLabel(left.platform).localeCompare(
              platformLabel(right.platform),
            ) ||
            left.id.localeCompare(right.id),
        ),
      }));
  }, [rules]);

  const enabledRulesCount = useMemo(
    () => rules.filter((rule) => rule.enabled).length,
    [rules],
  );
  const activeFilterCount = useMemo(
    () => Object.values(filters).filter((value) => value !== undefined).length,
    [filters],
  );
  const hasAnyFilter =
    activeFilterCount > 0 ||
    Boolean(draftKey.trim() || draftPlatform || draftEnabled !== "all");
  const numberValueError =
    form.valueType === "number" ? validateNumberValue(form.valueText) : "";
  const jsonValueError =
    form.valueType === "object" || form.valueType === "array"
      ? validateJsonValue(form.valueText, form.valueType)
      : "";
  const keyValueError = editingRule ? "" : validateKeyValue(form.key);
  const visibleKeyError = keyTouched ? keyValueError : "";
  const minVersionError = validateVersionValue(form.minAppVersion);
  const maxVersionError = validateVersionValue(form.maxAppVersion);
  const versionRangeError =
    !minVersionError &&
    !maxVersionError &&
    form.minAppVersion &&
    form.maxAppVersion &&
    compareVersions(form.minAppVersion, form.maxAppVersion) > 0
      ? "Минимальная версия не может быть больше максимальной"
      : "";
  const resolveVersionError = validateVersionValue(resolveVersion);

  const openCreate = () => {
    setEditingRule(null);
    setForm({ ...EMPTY_FORM });
    setKeyTouched(false);
    setDialogOpen(true);
  };

  const openEdit = async (rule: RemoteConfigRule) => {
    setLoadingRuleId(rule.id);
    try {
      const freshRule = await remoteConfigProxy.getOne(rule.id);
      setEditingRule(freshRule);
      setForm(ruleToForm(freshRule));
      setKeyTouched(false);
      setDialogOpen(true);
    } catch (error) {
      notify(errorMessage(error, "загрузить правило"), { type: "error" });
      if (error instanceof ApiGatewayError && error.status === 404)
        await loadRules(filters);
    } finally {
      setLoadingRuleId(null);
    }
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditingRule(null);
    setForm({ ...EMPTY_FORM });
    setKeyTouched(false);
  };

  const saveRule = async () => {
    const validationError = validateForm(form, editingRule);
    if (validationError) {
      notify(validationError, { type: "warning" });
      return;
    }

    setSaving(true);
    try {
      if (editingRule) {
        const patch = buildPatch(editingRule, form);
        if (Object.keys(patch).length === 0) {
          notify("В правиле нет изменений", { type: "warning" });
          return;
        }
        await remoteConfigProxy.update(editingRule.id, patch);
        notify("Правило обновлено", { type: "info" });
      } else {
        const payload: CreateRemoteConfigRuleRequest = {
          key: form.key.trim(),
          value: parseValue(form),
          platform: form.platform || null,
          minAppVersion: form.minAppVersion.trim() || null,
          maxAppVersion: form.maxAppVersion.trim() || null,
          priority: Number(form.priority),
          enabled: form.enabled,
          description: form.description || null,
        };
        await remoteConfigProxy.create(payload);
        notify("Правило создано", { type: "info" });
      }
      setDialogOpen(false);
      setEditingRule(null);
      setForm({ ...EMPTY_FORM });
      await refreshAfterMutation();
    } catch (error) {
      console.error("Remote config save error:", error);
      notify(errorMessage(error, "сохранить правило"), { type: "error" });
      if (error instanceof ApiGatewayError && error.status === 404)
        await loadRules(filters);
    } finally {
      setSaving(false);
    }
  };

  const deleteRule = async () => {
    if (!deletingRule) return;
    setSaving(true);
    try {
      await remoteConfigProxy.delete(deletingRule.id);
      notify("Правило удалено", { type: "info" });
      setDeletingRule(null);
      await refreshAfterMutation();
    } catch (error) {
      console.error("Remote config delete error:", error);
      notify(errorMessage(error, "удалить правило"), { type: "error" });
      if (error instanceof ApiGatewayError && error.status === 404) {
        setDeletingRule(null);
        await loadRules(filters);
      }
    } finally {
      setSaving(false);
    }
  };

  const applyFilters = () =>
    setFilters({
      key: draftKey.trim() || undefined,
      platform: draftPlatform || undefined,
      enabled: draftEnabled === "all" ? undefined : draftEnabled === "true",
    });

  const resetFilters = () => {
    setDraftKey("");
    setDraftPlatform("");
    setDraftEnabled("all");
    setFilters({});
  };

  const getResolveParams = (requirePlatform: boolean) => {
    const version = resolveVersion.trim();
    if (requirePlatform && !resolvePlatform) {
      notify("Выберите платформу для формирования JSON-файла", {
        type: "warning",
      });
      return null;
    }
    if (resolveVersionError) {
      notify(resolveVersionError, {
        type: "warning",
      });
      return null;
    }

    return {
      platform: resolvePlatform || undefined,
      appVersion: version || undefined,
    } satisfies ResolveParams;
  };

  const handleResolve = () => {
    const params = getResolveParams(false);
    if (params) void runResolve(params);
  };

  const handleDownload = async () => {
    const params = getResolveParams(true);
    if (!params?.platform) return;

    setDownloadLoading(true);
    let downloadUrl: string | null = null;
    try {
      const data = (await remoteConfigProxy.resolve(params)) ?? {};
      setResolveResult(data);
      setLastResolveParams(params);

      const fileContents = `${JSON.stringify(data, null, 2)}\n`;
      const blob = new Blob([fileContents], {
        type: "application/json;charset=utf-8",
      });
      downloadUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = buildRemoteConfigFileName(
        params.platform,
        params.appVersion,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      notify("JSON-файл сформирован", { type: "info" });
    } catch (error) {
      console.error("Remote config download error:", error);
      notify(errorMessage(error, "сформировать JSON-файл"), {
        type: "error",
      });
    } finally {
      if (downloadUrl) URL.revokeObjectURL(downloadUrl);
      setDownloadLoading(false);
    }
  };

  const copyResolveResult = async () => {
    if (resolveResult === null) return;
    try {
      await navigator.clipboard.writeText(
        JSON.stringify(resolveResult, null, 2),
      );
      notify("JSON скопирован", { type: "info" });
    } catch (error) {
      console.error("Remote config copy error:", error);
      notify("Не удалось скопировать JSON", { type: "error" });
    }
  };

  if (isPending)
    return (
      <Box sx={{ p: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  if (!canRead)
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Недостаточно прав для просмотра Remote Config
        </Alert>
      </Box>
    );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1480, mx: "auto" }}>
      <Title title="Remote Config" />

      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, md: 3 },
          mb: 2.5,
          borderRadius: 3,
          background:
            "linear-gradient(135deg, rgba(25,118,210,0.08), rgba(25,118,210,0.01) 65%)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
          spacing={2}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 52,
                height: 52,
                display: "grid",
                placeItems: "center",
                borderRadius: 2.5,
                bgcolor: "primary.main",
                color: "primary.contrastText",
                flexShrink: 0,
              }}
            >
              <TuneIcon fontSize="large" />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Remote Config
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Управление конфигурацией приложений по платформам и версиям
              </Typography>
            </Box>
          </Stack>
          {canWrite ? (
            <Button
              variant="contained"
              size="large"
              startIcon={<AddCircleOutlineIcon />}
              onClick={openCreate}
              sx={{ flexShrink: 0 }}
            >
              Новое правило
            </Button>
          ) : (
            <Chip label="Режим просмотра" variant="outlined" />
          )}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ mb: 2.5, borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value: "rules" | "resolve" | "audit") =>
            setActiveTab(value)
          }
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 1 }}
        >
          <Tab
            value="rules"
            icon={<ListAltIcon />}
            iconPosition="start"
            label="Правила"
          />
          <Tab
            value="resolve"
            icon={<ScienceOutlinedIcon />}
            iconPosition="start"
            label="Проверка и экспорт"
          />
          {canWrite ? (
            <Tab
              value="audit"
              icon={<HistoryOutlinedIcon />}
              iconPosition="start"
              label="Аудит"
            />
          ) : null}
        </Tabs>
      </Paper>

      {activeTab === "rules" ? (
        <Stack spacing={2.5}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(3, minmax(0, 1fr))",
              },
              gap: 1.5,
            }}
          >
            <StatTile
              label="Ключей"
              value={groups.length}
              icon={<DataObjectIcon />}
            />
            <StatTile
              label="Всего правил"
              value={rules.length}
              icon={<ListAltIcon />}
            />
            <StatTile
              label="Включено"
              value={enabledRulesCount}
              icon={<CheckCircleOutlineIcon />}
              color="success.main"
            />
          </Box>

          <Paper
            variant="outlined"
            sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2.5 }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1.5 }}
            >
              <FilterAltOutlinedIcon color="action" />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Фильтры
              </Typography>
              {activeFilterCount > 0 ? (
                <Chip label={activeFilterCount} size="small" color="primary" />
              ) : null}
            </Stack>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "minmax(0, 2fr) minmax(160px, 1fr) minmax(150px, 1fr)",
                },
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                label="Ключ"
                placeholder="checkout_ui"
                fullWidth
                value={draftKey}
                onChange={(event) => setDraftKey(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") applyFilters();
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: 0,
                  gridColumn: { sm: "1 / -1", lg: "auto" },
                }}
              />
              <FormControl size="small" fullWidth>
                <InputLabel id="remote-config-platform-filter" shrink>
                  Платформа
                </InputLabel>
                <Select
                  labelId="remote-config-platform-filter"
                  label="Платформа"
                  value={draftPlatform}
                  displayEmpty
                  renderValue={(value) =>
                    value ? platformLabel(value as Platform) : "Все платформы"
                  }
                  onChange={(event) =>
                    setDraftPlatform(event.target.value as Platform | "")
                  }
                >
                  <MenuItem value="">Все платформы</MenuItem>
                  <MenuItem value="ANDROID">Android</MenuItem>
                  <MenuItem value="IOS">iOS</MenuItem>
                  <MenuItem value="WEB">Web</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel id="remote-config-enabled-filter">
                  Статус
                </InputLabel>
                <Select
                  labelId="remote-config-enabled-filter"
                  label="Статус"
                  value={draftEnabled}
                  onChange={(event) =>
                    setDraftEnabled(
                      event.target.value as "all" | "true" | "false",
                    )
                  }
                >
                  <MenuItem value="all">Любой статус</MenuItem>
                  <MenuItem value="true">Включены</MenuItem>
                  <MenuItem value="false">Выключены</MenuItem>
                </Select>
              </FormControl>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "stretch", sm: "flex-end" },
                  gap: 0.5,
                  gridColumn: { xs: "auto", sm: "1 / -1" },
                }}
              >
                <Button
                  variant="contained"
                  onClick={applyFilters}
                  disabled={loading}
                  sx={{ minWidth: 92, flex: { xs: 1, sm: "initial" } }}
                >
                  Найти
                </Button>
                <Button
                  variant="text"
                  onClick={resetFilters}
                  disabled={loading || !hasAnyFilter}
                  sx={{ minWidth: 92, flex: { xs: 1, sm: "initial" } }}
                >
                  Сбросить
                </Button>
                <Tooltip title="Обновить список">
                  <span>
                    <IconButton
                      onClick={() => void loadRules(filters)}
                      disabled={loading}
                      aria-label="Обновить список"
                    >
                      <RefreshIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </Paper>

          {loadError ? (
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => void loadRules(filters)}
                >
                  Повторить
                </Button>
              }
            >
              {loadError}
            </Alert>
          ) : null}

          {loading ? (
            <Stack spacing={1.5}>
              {[0, 1, 2].map((item) => (
                <Skeleton
                  key={item}
                  variant="rounded"
                  height={72}
                  sx={{ borderRadius: 2 }}
                />
              ))}
            </Stack>
          ) : groups.length === 0 && !loadError ? (
            <Paper
              variant="outlined"
              sx={{ p: 5, textAlign: "center", borderRadius: 2 }}
            >
              <DataObjectIcon
                sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
              />
              <Typography variant="h6">Правила не найдены</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Измените фильтры или создайте первое правило конфигурации.
              </Typography>
              {canWrite ? (
                <Button
                  variant="outlined"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={openCreate}
                >
                  Создать правило
                </Button>
              ) : null}
            </Paper>
          ) : (
            <Stack spacing={1.5}>
              {groups.map((group) => {
                const enabledInGroup = group.items.filter(
                  (rule) => rule.enabled,
                ).length;
                return (
                  <Accordion
                    key={group.key}
                    disableGutters
                    elevation={0}
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: "12px !important",
                      overflow: "hidden",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        px: 2.5,
                        minHeight: 68,
                        "& .MuiAccordionSummary-content": { my: 1.5 },
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={{ xs: 0.75, sm: 1.5 }}
                        alignItems={{ sm: "center" }}
                        sx={{ minWidth: 0, flex: 1 }}
                      >
                        <Typography
                          component="code"
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "1rem",
                            fontWeight: 700,
                            color: "primary.main",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {group.key}
                        </Typography>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap">
                          <Chip
                            label={`${group.items.length} ${group.items.length === 1 ? "правило" : "правил"}`}
                            size="small"
                          />
                          <Chip
                            label={`${enabledInGroup} включено`}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        </Stack>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{ p: 0, borderTop: 1, borderColor: "divider" }}
                    >
                      <Stack divider={<Divider flexItem />}>
                        {group.items.map((rule) => (
                          <Box
                            key={rule.id}
                            sx={{
                              p: { xs: 2, md: 2.5 },
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "1fr",
                                md: "210px minmax(0, 1fr) auto",
                              },
                              gap: 2,
                              alignItems: "start",
                              bgcolor: rule.enabled
                                ? "background.paper"
                                : "action.hover",
                            }}
                          >
                            <Stack spacing={1} alignItems="flex-start">
                              <Stack
                                direction="row"
                                spacing={0.75}
                                flexWrap="wrap"
                              >
                                <Chip
                                  label={platformLabel(rule.platform)}
                                  size="small"
                                  color={rule.platform ? "primary" : "default"}
                                  variant={
                                    rule.platform ? "filled" : "outlined"
                                  }
                                />
                                <Chip
                                  label={
                                    rule.enabled ? "Включено" : "Выключено"
                                  }
                                  size="small"
                                  color={rule.enabled ? "success" : "default"}
                                  variant="outlined"
                                />
                              </Stack>
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Версии приложения
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {versionRangeLabel(rule)}
                                </Typography>
                              </Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Приоритет: <strong>{rule.priority}</strong>
                              </Typography>
                            </Stack>

                            <Stack spacing={1.25} sx={{ minWidth: 0 }}>
                              {rule.description ? (
                                <Typography variant="body2">
                                  {rule.description}
                                </Typography>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.disabled"
                                >
                                  Без описания
                                </Typography>
                              )}
                              <Box
                                sx={{
                                  borderRadius: 1.5,
                                  bgcolor: "grey.900",
                                  color: "grey.100",
                                  overflow: "hidden",
                                }}
                              >
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  sx={{
                                    px: 1.5,
                                    py: 0.75,
                                    bgcolor: "grey.800",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "grey.400" }}
                                  >
                                    VALUE ·{" "}
                                    {valueTypeLabel[getValueType(rule.value)]}
                                  </Typography>
                                </Stack>
                                <Box
                                  component="pre"
                                  sx={{
                                    m: 0,
                                    p: 1.5,
                                    maxHeight: 180,
                                    overflow: "auto",
                                    whiteSpace: "pre-wrap",
                                    overflowWrap: "anywhere",
                                    fontSize: "0.78rem",
                                    lineHeight: 1.55,
                                  }}
                                >
                                  {formatRuleValue(rule.value)}
                                </Box>
                              </Box>
                            </Stack>

                            {canWrite ? (
                              <Stack
                                direction="row"
                                spacing={0.5}
                                justifyContent="flex-end"
                              >
                                <Tooltip title="Редактировать">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => void openEdit(rule)}
                                      disabled={
                                        loadingRuleId === rule.id || saving
                                      }
                                      aria-label={`Редактировать ${rule.key}`}
                                    >
                                      {loadingRuleId === rule.id ? (
                                        <CircularProgress size={18} />
                                      ) : (
                                        <EditIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Удалить">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => setDeletingRule(rule)}
                                      disabled={saving}
                                      aria-label={`Удалить ${rule.key}`}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Stack>
                            ) : null}
                          </Box>
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>
          )}
        </Stack>
      ) : activeTab === "resolve" ? (
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            maxWidth: 980,
            mx: "auto",
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="flex-start"
            sx={{ mb: 3 }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                display: "grid",
                placeItems: "center",
                borderRadius: 2,
                bgcolor: "primary.main",
                color: "primary.contrastText",
                flexShrink: 0,
              }}
            >
              <ScienceOutlinedIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Проверка итоговой конфигурации
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Посмотрите конфиг глазами конкретного клиента или скачайте его
                как fallback-файл.
              </Typography>
            </Box>
          </Stack>

          <Alert severity="info" sx={{ mb: 2.5 }}>
            Без платформы применяются только глобальные правила. Без версии —
            только правила без ограничений по версии.
          </Alert>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
              },
              gap: 1.5,
              alignItems: "start",
            }}
          >
            <FormControl size="small" fullWidth>
              <InputLabel id="remote-config-resolve-platform" shrink>
                Платформа клиента
              </InputLabel>
              <Select
                labelId="remote-config-resolve-platform"
                label="Платформа клиента"
                value={resolvePlatform}
                displayEmpty
                renderValue={(value) =>
                  value
                    ? platformLabel(value as Platform)
                    : "Только глобальные правила"
                }
                onChange={(event) =>
                  setResolvePlatform(event.target.value as Platform | "")
                }
              >
                <MenuItem value="">Только глобальные правила</MenuItem>
                <MenuItem value="ANDROID">Android</MenuItem>
                <MenuItem value="IOS">iOS</MenuItem>
                <MenuItem value="WEB">Web</MenuItem>
              </Select>
              <FormHelperText>Для скачивания выберите платформу</FormHelperText>
            </FormControl>
            <TextField
              size="small"
              label="Версия приложения"
              placeholder="2.5.1"
              value={resolveVersion}
              onChange={(event) =>
                setResolveVersion(normalizeVersionInput(event.target.value))
              }
              error={Boolean(resolveVersionError)}
              helperText={resolveVersionError || "Необязательно · формат 2.5.1"}
              inputProps={{ inputMode: "numeric", pattern: "[0-9.]*" }}
              fullWidth
            />
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{
                gridColumn: { xs: "auto", sm: "1 / -1" },
                justifyContent: { sm: "flex-end" },
                pt: 0.5,
              }}
            >
              <Button
                variant="outlined"
                startIcon={
                  resolveLoading ? (
                    <CircularProgress size={18} />
                  ) : (
                    <ScienceOutlinedIcon />
                  )
                }
                onClick={handleResolve}
                disabled={
                  resolveLoading ||
                  downloadLoading ||
                  Boolean(resolveVersionError)
                }
                sx={{
                  minWidth: 132,
                  width: { xs: "100%", sm: "auto" },
                  whiteSpace: "nowrap",
                }}
              >
                Проверить
              </Button>
              <Button
                variant="contained"
                startIcon={
                  downloadLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <DownloadIcon />
                  )
                }
                onClick={() => void handleDownload()}
                disabled={
                  resolveLoading ||
                  downloadLoading ||
                  Boolean(resolveVersionError)
                }
                sx={{
                  minWidth: 164,
                  width: { xs: "100%", sm: "auto" },
                  whiteSpace: "nowrap",
                }}
              >
                {downloadLoading ? "Формирую..." : "Скачать JSON"}
              </Button>
            </Stack>
          </Box>

          {resolveResult !== null ? (
            <Box sx={{ mt: 3 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Результат resolve
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Ключей в конфигурации: {Object.keys(resolveResult).length}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => void copyResolveResult()}
                >
                  Копировать
                </Button>
              </Stack>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 2.5,
                  overflow: "auto",
                  maxHeight: 520,
                  bgcolor: "grey.900",
                  color: "grey.100",
                  borderRadius: 2,
                  fontSize: "0.82rem",
                  lineHeight: 1.6,
                }}
              >
                {JSON.stringify(resolveResult, null, 2)}
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                mt: 3,
                p: 4,
                textAlign: "center",
                border: 1,
                borderStyle: "dashed",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <ScienceOutlinedIcon
                sx={{ color: "text.disabled", fontSize: 40 }}
              />
              <Typography variant="body2" color="text.secondary">
                Выберите параметры и нажмите «Проверить».
              </Typography>
            </Box>
          )}
        </Paper>
      ) : (
        <Stack spacing={1.5} sx={{ maxWidth: 1120, mx: "auto" }}>
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: 2.5,
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                display: "grid",
                placeItems: "center",
                borderRadius: 2,
                bgcolor: "primary.main",
                color: "primary.contrastText",
                flexShrink: 0,
              }}
            >
              <HistoryOutlinedIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Аудит действий
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Создание, удаление и изменения правил за последние 180 дней.
              </Typography>
            </Box>
          </Paper>

          {auditLoading ? (
            <Stack spacing={1.5}>
              {[0, 1, 2].map((item) => (
                <Skeleton
                  key={item}
                  variant="rounded"
                  height={86}
                  sx={{ borderRadius: 2 }}
                />
              ))}
            </Stack>
          ) : auditItems.length === 0 && !auditError ? (
            <Paper
              variant="outlined"
              sx={{ p: 5, textAlign: "center", borderRadius: 2.5 }}
            >
              <HistoryOutlinedIcon
                sx={{ fontSize: 46, color: "text.disabled", mb: 1 }}
              />
              <Typography variant="h6">История пока пуста</Typography>
              <Typography variant="body2" color="text.secondary">
                Здесь появятся новые изменения правил Remote Config.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={1.25}>
              {auditItems.map((item) => {
                const actionMeta = AUDIT_ACTION_META[item.action] ?? {
                  label: item.action,
                  color: "default" as const,
                };
                const snapshot =
                  item.action === "DELETED"
                    ? item.beforeSnapshot
                    : item.afterSnapshot;
                return (
                  <Accordion
                    key={item.id}
                    disableGutters
                    elevation={0}
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: "12px !important",
                      overflow: "hidden",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        px: { xs: 2, md: 2.5 },
                        minHeight: 80,
                        "& .MuiAccordionSummary-content": { my: 1.5 },
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={{ xs: 0.75, md: 1.5 }}
                        alignItems={{ md: "center" }}
                        sx={{ minWidth: 0, flex: 1 }}
                      >
                        <Chip
                          label={actionMeta.label}
                          color={actionMeta.color}
                          size="small"
                          sx={{ alignSelf: { xs: "flex-start", md: "center" } }}
                        />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            component="code"
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: 700,
                              color: "primary.main",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {item.ruleKey || "Ключ неизвестен"}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ overflowWrap: "anywhere" }}
                          >
                            {formatAuditActor(item)} ·{" "}
                            {formatAuditDate(item.createdAt)}
                          </Typography>
                        </Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ overflowWrap: "anywhere" }}
                        >
                          ID: {item.ruleId || "—"}
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        p: { xs: 2, md: 2.5 },
                        borderTop: 1,
                        borderColor: "divider",
                        bgcolor: "action.hover",
                      }}
                    >
                      {item.changes.length > 0 ? (
                        <Stack spacing={1.5}>
                          {item.changes.map((change, index) => (
                            <Box key={`${change.path}-${index}`}>
                              <Typography
                                component="code"
                                variant="body2"
                                sx={{
                                  display: "block",
                                  mb: 0.75,
                                  fontFamily: "monospace",
                                  fontWeight: 700,
                                }}
                              >
                                {change.path}
                              </Typography>
                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: {
                                    xs: "minmax(0, 1fr)",
                                    md: "repeat(2, minmax(0, 1fr))",
                                  },
                                  gap: 1,
                                }}
                              >
                                <Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    До
                                  </Typography>
                                  <AuditValue
                                    state={change.before}
                                    missingLabel="Отсутствовало"
                                  />
                                </Box>
                                <Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    После
                                  </Typography>
                                  <AuditValue
                                    state={change.after}
                                    missingLabel="Удалено"
                                  />
                                </Box>
                              </Box>
                            </Box>
                          ))}
                        </Stack>
                      ) : snapshot ? (
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{ mb: 1, fontWeight: 700 }}
                          >
                            {item.action === "DELETED"
                              ? "Удалённое правило"
                              : "Созданное правило"}
                          </Typography>
                          <AuditSnapshot rule={snapshot} />
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Детали изменения недоступны.
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}

              {auditError ? (
                <Alert
                  severity="error"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() =>
                        void loadAudit(
                          auditItems.length > 0 ? auditNextCursor : null,
                          auditItems.length === 0,
                        )
                      }
                    >
                      Повторить
                    </Button>
                  }
                >
                  {auditError}
                </Alert>
              ) : null}

              {auditHasNextPage && !auditError ? (
                <Box
                  ref={auditSentinelRef}
                  sx={{
                    minHeight: 56,
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  {auditLoadingMore ? <CircularProgress size={24} /> : null}
                </Box>
              ) : auditItems.length > 0 && !auditError ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  align="center"
                  sx={{ py: 1 }}
                >
                  Все события загружены
                </Typography>
              ) : null}
            </Stack>
          )}
        </Stack>
      )}

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: { borderRadius: 3, overflow: "hidden" },
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: "divider" }}>
          {editingRule
            ? `Редактировать ${editingRule.key}`
            : "Новое правило Remote Config"}
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "background.default", p: 0 }}>
          <Stack spacing={2.5} sx={{ p: { xs: 2, sm: 3 } }}>
            {editingRule ? (
              <Alert severity="info">
                Ключ нельзя изменить. Уже заданные платформу, версии и описание
                нельзя очистить из-за ограничений PATCH API.
              </Alert>
            ) : null}

            <FormSection
              title="Основные настройки"
              description="Ключ определяет параметр, а платформа — область действия правила."
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    sm: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                <TextField
                  label="Ключ конфигурации"
                  required
                  fullWidth
                  value={form.key}
                  disabled={Boolean(editingRule)}
                  onChange={(event) => {
                    setKeyTouched(true);
                    setForm((current) => ({
                      ...current,
                      key: event.target.value,
                    }));
                  }}
                  onBlur={() => setKeyTouched(true)}
                  error={Boolean(visibleKeyError)}
                  helperText={
                    visibleKeyError ||
                    "Начните с a–z; далее используйте a–z, 0–9 и _"
                  }
                  inputProps={{ maxLength: 64 }}
                />
                <FormControl fullWidth>
                  <InputLabel id="remote-config-form-platform" shrink>
                    Платформа
                  </InputLabel>
                  <Select
                    labelId="remote-config-form-platform"
                    label="Платформа"
                    value={form.platform}
                    displayEmpty
                    renderValue={(value) =>
                      value
                        ? platformLabel(value as Platform)
                        : "Все платформы · глобальное правило"
                    }
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        platform: event.target.value as Platform | "",
                      }))
                    }
                  >
                    <MenuItem
                      value=""
                      disabled={Boolean(
                        editingRule && editingRule.platform !== null,
                      )}
                    >
                      Все платформы
                    </MenuItem>
                    <MenuItem value="ANDROID">Android</MenuItem>
                    <MenuItem value="IOS">iOS</MenuItem>
                    <MenuItem value="WEB">Web</MenuItem>
                  </Select>
                  <FormHelperText>
                    Ограничивает правило выбранной платформой
                  </FormHelperText>
                </FormControl>
                <TextField
                  label="Описание"
                  placeholder="Для чего используется это правило"
                  value={form.description}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      description: event.target.value,
                    }))
                  }
                  multiline
                  minRows={2}
                  helperText={`${form.description.length}/512`}
                  inputProps={{ maxLength: 512 }}
                  sx={{ gridColumn: "1 / -1" }}
                />
                <Box
                  sx={{
                    gridColumn: "1 / -1",
                    px: 1.5,
                    py: 1,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 2,
                    bgcolor: "action.hover",
                  }}
                >
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Switch
                        checked={form.enabled}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            enabled: event.target.checked,
                          }))
                        }
                      />
                    }
                    label={
                      form.enabled ? "Правило активно" : "Правило выключено"
                    }
                  />
                </Box>
              </Box>
            </FormSection>

            <FormSection
              title="Условия применения"
              description="Оставьте версии пустыми, чтобы правило работало для всех версий приложения."
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(2, minmax(0, 1fr)) minmax(170px, 0.65fr)",
                  },
                  gap: 2,
                  alignItems: "start",
                }}
              >
                <TextField
                  label="Минимальная версия"
                  placeholder="1.0.0"
                  fullWidth
                  error={Boolean(minVersionError || versionRangeError)}
                  helperText={
                    minVersionError ||
                    versionRangeError ||
                    "Без нижней границы, если пусто"
                  }
                  value={form.minAppVersion}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      minAppVersion: normalizeVersionInput(event.target.value),
                    }))
                  }
                  inputProps={{ inputMode: "numeric", pattern: "[0-9.]*" }}
                />
                <TextField
                  label="Максимальная версия"
                  placeholder="2.0.0"
                  fullWidth
                  error={Boolean(maxVersionError || versionRangeError)}
                  helperText={
                    maxVersionError ||
                    versionRangeError ||
                    "Без верхней границы, если пусто"
                  }
                  value={form.maxAppVersion}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      maxAppVersion: normalizeVersionInput(event.target.value),
                    }))
                  }
                  inputProps={{ inputMode: "numeric", pattern: "[0-9.]*" }}
                />
                <TextField
                  label="Приоритет"
                  helperText="От −10000 до 10000"
                  fullWidth
                  type="number"
                  value={form.priority}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      priority: event.target.value,
                    }))
                  }
                  inputProps={{ min: -10000, max: 10000, step: 1 }}
                />
              </Box>
            </FormSection>

            <FormSection
              title="Значение"
              description="Выберите тип и задайте значение, которое получит приложение."
            >
              <Stack spacing={2}>
                <FormControl fullWidth sx={{ maxWidth: 320 }}>
                  <InputLabel id="remote-config-value-type">
                    Тип значения
                  </InputLabel>
                  <Select
                    labelId="remote-config-value-type"
                    label="Тип значения"
                    value={form.valueType}
                    onChange={(event) => {
                      const valueType = event.target.value as ValueType;
                      setForm((current) => ({
                        ...current,
                        valueType,
                        ...emptyValueForType(valueType),
                      }));
                    }}
                  >
                    {SELECTABLE_VALUE_TYPES.map((type) => (
                      <MenuItem
                        key={type}
                        value={type}
                        disabled={
                          type === "null" &&
                          Boolean(editingRule && editingRule.value !== null)
                        }
                      >
                        {valueTypeLabel[type]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {form.valueType === "object" || form.valueType === "array" ? (
                  <Suspense
                    fallback={
                      <Skeleton variant="rounded" width="100%" height={284} />
                    }
                  >
                    <JsonCodeEditor
                      value={form.valueText}
                      expectedType={form.valueType}
                      errorMessage={jsonValueError}
                      onChange={(value) =>
                        setForm((current) => ({
                          ...current,
                          valueText: value,
                        }))
                      }
                    />
                  </Suspense>
                ) : form.valueType === "string" ? (
                  <TextField
                    label="Строковое значение"
                    value={form.valueText}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        valueText: event.target.value,
                      }))
                    }
                    multiline
                    minRows={3}
                    helperText={`${form.valueText.length}/4096`}
                    inputProps={{ maxLength: 4096 }}
                  />
                ) : form.valueType === "number" ? (
                  <TextField
                    label="Числовое значение"
                    type="number"
                    value={form.valueText}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        valueText: event.target.value,
                      }))
                    }
                    error={Boolean(numberValueError)}
                    helperText={
                      numberValueError ||
                      "Поддерживаются дробные и отрицательные числа"
                    }
                    inputProps={{ step: "any" }}
                    sx={{ maxWidth: 320 }}
                  />
                ) : form.valueType === "boolean" ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      minHeight: 56,
                      px: 1.5,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 2,
                      bgcolor: "action.hover",
                    }}
                  >
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={
                        <Switch
                          checked={form.booleanValue}
                          onChange={(event) =>
                            setForm((current) => ({
                              ...current,
                              booleanValue: event.target.checked,
                            }))
                          }
                        />
                      }
                      label={
                        form.booleanValue ? "Значение: true" : "Значение: false"
                      }
                    />
                  </Box>
                ) : (
                  <Alert severity="info">
                    Приложение получит JSON-значение null.
                  </Alert>
                )}
              </Stack>
            </FormSection>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            gap: 1,
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Button onClick={closeDialog} disabled={saving}>
            Отмена
          </Button>
          <Button
            variant="contained"
            onClick={() => void saveRule()}
            disabled={
              saving ||
              Boolean(
                keyValueError ||
                  minVersionError ||
                  maxVersionError ||
                  versionRangeError ||
                  jsonValueError ||
                  numberValueError,
              )
            }
            sx={{ minWidth: 120 }}
          >
            {saving ? <CircularProgress size={20} /> : "Сохранить"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deletingRule)}
        onClose={() => {
          if (!saving) setDeletingRule(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Удалить правило?</DialogTitle>
        <DialogContent>
          {deletingRule ? (
            <Alert severity="warning">
              Ключ: <strong>{deletingRule.key}</strong>
              <br />
              Область: {scopeLabel(deletingRule)}. Это действие нельзя отменить.
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingRule(null)} disabled={saving}>
            Отмена
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void deleteRule()}
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : "Удалить"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
